import * as fs from 'fs';
import { isEmpty, isNumber, isString, isUndefined } from "lodash";
import * as os from 'os';
import path from "path";
import { PlayPositions, ScenarioActionType } from "../enums";
import { Point, ScenarioAction, ScenarioBallFrame, ScenarioBallSegment, ScenarioData, ScenarioPlayer, ScenarioWaypoint } from "../types/ScenarioTypes";
import { DefaultGeneratorSettings, GeneratorSettings, ImportStreamContext, StandardEvent, StandardMatchData, StandardMatchFrame, StandardPlayerData, StandardPlayerFrame } from "../types/TrackingTypes";

export const getScenarioPointAtTime = (waypoints: ScenarioWaypoint[], time: number): ScenarioWaypoint => {
  if (waypoints.length === 0)
    return { WorldPosition: { x: 0, y: 0 }, PreviousTangent: { x: 0, y: 0 }, NextTangent: { x: 0, y: 0 }, Time: 0 };

  if (waypoints.length === 1)
    return waypoints[0];

  if (time <= waypoints[0].Time)
    return waypoints[0];

  if (time >= waypoints[waypoints.length - 1].Time)
    return waypoints[waypoints.length - 1];

  for (let i = 0; i < waypoints.length - 1; i++) {
    if (waypoints[i].Time <= time && waypoints[i + 1].Time >= time) {
      const t = (time - waypoints[i].Time) / (waypoints[i + 1].Time - waypoints[i].Time);
      const position = lerpPoint(waypoints[i].WorldPosition, waypoints[i + 1].WorldPosition, t);
      const prev = lerpPoint(waypoints[i].PreviousTangent, waypoints[i + 1].PreviousTangent, t);
      const next = lerpPoint(waypoints[i].NextTangent, waypoints[i + 1].NextTangent, t);
      return { WorldPosition: position, PreviousTangent: prev, NextTangent: next, Time: time };
    }
  }
  return waypoints[0];
};

export const getNearestScenarioPlayer = (players: ScenarioPlayer[], position: Point, time: number) => {
  let minDistance = 1000000;
  let nearest = -1;

  players.forEach((player, key) => {
    const playerPos = getScenarioPointAtTime(player.Route.Waypoints, time);
    const distance = getDistance(playerPos.WorldPosition, position);
    if (distance < minDistance) {
      minDistance = distance;
      nearest = key;
    }
  });
  return players[nearest];
};

export const addWaypoint = (waypoints: ScenarioWaypoint[], time: number, position: Point) => {
  // Add waypoint to player
  let previousWaypoint;
  if (waypoints.length > 0)
    previousWaypoint = waypoints[waypoints.length - 1];
  else
    previousWaypoint = {
      Time: 0,
      WorldPosition: {
        x: position.x,
        y: position.y
      },
      PreviousTangent: {
        x: position.x,
        y: position.y
      },
      NextTangent: {
        x: position.x,
        y: position.y
      }
    };

  waypoints.push({
    Time: time,
    WorldPosition: {
      x: position.x,
      y: position.y
    },
    PreviousTangent: lerpPoint(previousWaypoint.WorldPosition, {
      x: position.x,
      y: position.y
    }, 0.5),
    NextTangent: {
      x: position.x,
      y: position.y
    }
  });

  if (waypoints.length > 1) {
    const lastWaypoint = waypoints[waypoints.length - 2];
    const lastWaypointPosition = {
      x: lastWaypoint.WorldPosition.x,
      y: lastWaypoint.WorldPosition.y
    }
    lastWaypoint.NextTangent = lerpPoint(lastWaypointPosition, waypoints[waypoints.length - 1].WorldPosition, 0.5);
  }
};

export const transformPosition = (x: number, y: number, mirrorX: boolean, mirrorY: boolean) => {
  let newX = mirrorY ? -x : x; // not a typo: axes are reversed
  let newY = mirrorX ? -y : y; // not a typo: axes are reversed
  return { newX, newY };
};


const calculateVelocity = (frame1: ScenarioBallFrame, frame2: ScenarioBallFrame, timeDiff: number): number => {
  if (timeDiff === 0) return 0;
  const distance = Math.sqrt(
    Math.pow(frame2.Position.x - frame1.Position.x, 2) +
    Math.pow(frame2.Position.y - frame1.Position.y, 2) +
    Math.pow(frame2.Position.z - frame1.Position.z, 2)
  );
  return distance / timeDiff;
};

type ChangeFrame = {
  time: number;
  rateOfChange: number;
}

const calculateAcceleration = (frame1: ChangeFrame, frame2: ChangeFrame, timeDiff: number): number => {
  if (timeDiff === 0) return 0;
  return (frame2.rateOfChange - frame1.rateOfChange) / timeDiff;
};

const calculateChanges = <T>(
  elements: T[],
  getTime: (element: T) => number,
  getRateOfChange: (a: T, b: T, t: number) => number,
): { time: number; rateOfChange: number }[] => {
  const ratesOfChange: { time: number; rateOfChange: number }[] = [];

  for (let i = 0; i < elements.length - 1; i++) {
    const currentElement = elements[i];
    const nextElement = elements[i + 1];

    ratesOfChange.push({ time: getTime(currentElement), rateOfChange: getRateOfChange(currentElement, nextElement, 1) });
  }

  return ratesOfChange;
};

const findBallAccelerationTime = (frames: ScenarioBallFrame[]): number => {
  if (frames.length < 3) return frames[0]?.Time || 0;

  const velocities: ChangeFrame[] = calculateChanges(
    frames,
    (frame) => frame.Time,
    calculateVelocity,
  );

  const accelerations: ChangeFrame[] = calculateChanges(
    velocities,
    (vel) => vel.time,
    calculateAcceleration,
  );

  if (accelerations.length === 0) return frames[0].Time;

  const maxAcceleration = accelerations.reduce(
    (max, acc) => acc.rateOfChange > max.rateOfChange ? acc : max,
    { time: frames[0].Time, rateOfChange: 0 }
  );

  return maxAcceleration.time;
};

export const addPassesFromBallFrames = (ballSegments: ScenarioBallSegment[], players: ScenarioPlayer[]) => {

  for (let i = 0; i < ballSegments.length; i++) {
    const segment = ballSegments[i];
    const fromPossessor = players.find(p => p.Hash === segment.fromPlayer);
    const toPossessor = players.find(p => p.Hash === segment.toPlayer);
    if (!fromPossessor || !toPossessor || segment.fromPlayer === segment.toPlayer) continue;

    // Find the actual acceleration time instead of using segment startTime
    const passTime = findBallAccelerationTime(segment.frames);
    let passHeight = segment.frames.reduce((max, f) => Math.max(max, f.Position.z), 0);

    const passAction: ScenarioAction = {
      enabled: true,
      type: ScenarioActionType.PassTarget,
      startTime: Math.max(0, passTime),
      endTime: passTime + 0.3,
      target: typeof segment.toPlayer === 'number' ? segment.toPlayer : -1,
      location: { WorldPosition: getScenarioPointAtTime(toPossessor.Route.Waypoints, passTime).WorldPosition },
      data: null,
      index: 0,
      height: passHeight,
      direction: 0,
      time: segment.endTime
    };

    fromPossessor.Route.Actions.push(passAction);
  }
};

export const getDistance = (point1: Point, point2: Point) => {
  return Math.sqrt(Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2));
};

/**
 * Compares two positions with floating-point tolerance to handle precision issues
 * @param pos1 - First position
 * @param pos2 - Second position  
 * @param tolerance - Tolerance for comparison (default: 0.0001)
 * @returns true if positions are within tolerance
 */
const isPositionEqual = (pos1: Point, pos2: Point, tolerance: number = 0.0001): boolean => {
  return Math.abs(pos1.x - pos2.x) < tolerance && 
         Math.abs(pos1.y - pos2.y) < tolerance;
};

/**
 * Finds the index of a frame in allBallFrames using tolerance-based position comparison
 * @param allBallFrames - Array of all ball frames
 * @param targetFrame - Frame to find
 * @param tolerance - Tolerance for position comparison (default: 0.0001)
 * @returns Index of the frame or -1 if not found
 */
const findFrameIndex = (allBallFrames: ScenarioBallFrame[], targetFrame: ScenarioBallFrame, tolerance: number = 0.0001): number => {
  return allBallFrames.findIndex(allFrame => 
    allFrame.Time === targetFrame.Time && 
    isPositionEqual(allFrame.Position, targetFrame.Position, tolerance)
  );
};

export const lerp = (a: number, b: number, t: number) => {
  const clampedT = Math.max(0, Math.min(1, t));
  return a + (b - a) * clampedT;
};

export const lerpPoint = (a: Point, b: Point, t: number) => {
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) };
};

export const passThroughResponseData = (response: any): Promise<any> => {
  if (response.code === 'ok') {
    return Promise.resolve(response.message);
  } else {
    return Promise.reject(new Error('Response code is not ok'));
  }
}

export const passThroughUploadData = (stream: ImportStreamContext, data: any) => {
  return Promise.resolve([isString(data) ? data : JSON.stringify(data)]);
};

export const getPositionDataPackets = (stream: ImportStreamContext, data: any, getTime: (frame: string, framerate: number) => number, framerate: number, maxPacketLength: number): Promise<any[]> => {

  // Split data into packets of maxPacketLength seconds
  // This algorithm assumes that the data is sorted by time and each frame is on a new line

  let lines = data.split('\n');
  let packetIndex = 0;
  let previousEndLine = '';
  const packets: string[] = [];

  console.log(`Processing ${lines.length} frames, startTime: ${stream.startTime}, maxPacketLength: ${maxPacketLength}`);
  stream.startTime = stream.startTime || getTime(lines[0], framerate) || 0;
  console.log(`Stream start time: ${stream.startTime}`);

  // Remove any frames that are before the start time of the stream
  const firstFrameIndex = lines.findIndex((l: string) => (getTime(l, framerate) - (stream.startTime as any)) >= 0);
  console.log(`First frame index: ${firstFrameIndex}`);
  if (firstFrameIndex > 0) {
    lines = lines.slice(firstFrameIndex);
    console.log(`Skipping ${firstFrameIndex} frames`);
  }

  // Keep splitting the data into packets until all frames are processed
  while (lines.length > 0 && packetIndex < (8000 / maxPacketLength)) {

    // Find the last frame that fits within the time span of the current packet
    const i = lines.findLastIndex((l: string) => (getTime(l, framerate) - (stream.startTime as any)) <= ((packetIndex + 1) * maxPacketLength));

    // If no frames exist in the time span, add the previous end line to the next packet to ensure at least one frame is present
    // If there is no previous end line, we are still at the start of the file, so add the first frame to the packet
    if (i === -1) {
      packets.push(previousEndLine !== '' ? previousEndLine : lines[0]);
    }

    // Else add all frames within the time span to the current packet
    else {
      const packetFrames = lines.splice(0, i + 1);
      previousEndLine = packetFrames[packetFrames.length - 1];
      packets.push(packetFrames.join('\n'));
    }

    packetIndex++;
  }

  // Record metadata about the stream so it can be stored in the db and used when we want to download the data later
  stream.storeCount = packets.length;
  stream.storeLength = maxPacketLength;

  console.log(`Split data into ${packets.length} packets`);
  return Promise.resolve(packets);
}

export const getDownloadPromise = (bucket: any, filePath: string, tempFilePath: string, stream: ImportStreamContext) => {

  return bucket.file(filePath).download({ destination: tempFilePath })
    .then((_: any) => fs.promises.readFile(tempFilePath || '', 'utf8'))
    .then((data: any) => {
      stream.data = data;
      return Promise.resolve(data);
    });
};

export const downloadCacheRecordData = async (stream: ImportStreamContext, request: any, bucket: any, cacheRecord: any, tempFiles: Array<string>) => {

  const filePath = `${cacheRecord.directory}${stream.fileName}-${0}.${stream.extension}`;
  const exists = await bucket.file(filePath).exists();
  if (!exists[0]) {
    console.log(`File ${filePath} does not exist`);
    return Promise.resolve('');
  }

  if (!stream.tempFilePath) {
    stream.tempFilePath = path.join(os.tmpdir(), stream.fileName);
  }
  tempFiles.push(stream.tempFilePath as string);
  return getDownloadPromise(bucket, filePath, stream.tempFilePath as string, stream);
};

export const downloadCacheRecordPositionData = async (stream: ImportStreamContext, request: any, bucket: any, cacheRecord: any, tempFiles: Array<string>) => {
  let startFileIndex = Math.max(0, Math.floor(request.start_time / cacheRecord.position_files_time));
  let endFileIndex = Math.min(cacheRecord.position_files_count - 1, Math.floor(request.end_time / cacheRecord.position_files_time));
  console.log('Downloading files', startFileIndex, 'to', endFileIndex);
  const downloadPromises: Promise<any>[] = [];
  for (let i = startFileIndex; i <= endFileIndex; i++) {
    const tempFile = `${stream.tempFilePath}-${i}`;
    tempFiles.push(tempFile);
    downloadPromises.push(getDownloadPromise(bucket, `${cacheRecord.directory}${stream.fileName}-${i}.${stream.extension}`, tempFile, stream));
  }
  const dataChunks = await Promise.all(downloadPromises);
  stream.data = dataChunks.join('\n');
  stream.startTime = cacheRecord.start_time || 0;
  return Promise.resolve();
}

const getWeightedDistanceToGoal = (player: StandardPlayerFrame, side: 'low' | 'high', metadata: StandardMatchData) => {
  // Get the squared distance from the player to the goal with a 2x bias on the Y axis
  const y = side === 'low' ? player.y + metadata.pitch_length / 2 : player.y - metadata.pitch_length / 2;
  return (2 * y * y) + (player.x * player.x);
}

const getGoalkeeperIdForFrame = (teamId: string, metadata: StandardMatchData, frame: StandardMatchFrame): string => {

  const lowestPlayerFrame: StandardPlayerFrame = frame.players.reduce((min: any, player: any) => getWeightedDistanceToGoal(player, 'low', metadata) < getWeightedDistanceToGoal(min, 'low', metadata) ? player : min, frame.players[0]);
  const lowestPlayer: StandardPlayerData | undefined = metadata.players.find((player: any) => player.id === lowestPlayerFrame.id);
  if (lowestPlayer && lowestPlayer.team_id === teamId)
    return lowestPlayer.id;

  const highestPlayerFrame: StandardPlayerFrame = frame.players.reduce((min: any, player: any) => getWeightedDistanceToGoal(player, 'high', metadata) < getWeightedDistanceToGoal(min, 'high', metadata) ? player : min, frame.players[0]);
  const highestPlayer: StandardPlayerData | undefined = metadata.players.find((player: any) => player.id === highestPlayerFrame.id);
  if (highestPlayer && highestPlayer.team_id === teamId)
    return highestPlayerFrame.id;

  console.log(`No goalkeeper found for team ${teamId} in frame ${frame.timestamp}`);
  return '';
}

export const getGoalkeeperAveragePosition = (frames: StandardMatchFrame[], goalkeeperId: string) => {
  const goalkeeperFrames = frames.filter(f => f.players.some((p: any) => p.id === goalkeeperId));
  if( goalkeeperFrames.length === 0 )
    return { x: 0, y: 0 };
  
  const averagePosition = goalkeeperFrames.reduce((sum: Point, frame) => {
    return {
      x: sum.x + (frame.players.find((p: any) => p.id === goalkeeperId)?.x || 0),
      y: sum.y + (frame.players.find((p: any) => p.id === goalkeeperId)?.y || 0)
    };
  }, { x: 0, y: 0 });
  
  return { x: averagePosition.x / goalkeeperFrames.length, y: averagePosition.y / goalkeeperFrames.length };
}

export const getGoalkeeperIdForTeam = (teamId: string, metadata: StandardMatchData, frames: StandardMatchFrame[]): string => {
  const metadataGoalkeeper = metadata.players.find((p: StandardPlayerData) => p.team_id === teamId && p.position === PlayPositions.GOALKEEPER);
  if (metadataGoalkeeper) {
    return metadataGoalkeeper.id;
  }
  // If theres no metadata for goalkeeper, find the player that is most often predicted to be the goalkeeper
  const goalkeeperIds = frames.map(f => getGoalkeeperIdForFrame(teamId, metadata, f));
  return goalkeeperIds.reduce((max: string, id) => goalkeeperIds.filter(i => i === id).length > goalkeeperIds.filter(i => i === max).length ? id : max, goalkeeperIds[0]);
}

export const guessGoalkeepers = (metadata: StandardMatchData, frames: StandardMatchFrame[]) => {
  // Guess who are goalkeepers based on player proximity to goals in the first 1000 frames
  const workingFrames = frames.slice(0, 1000);
  [metadata.home_team.id, metadata.away_team.id].forEach((teamId) => {
    const goalkeeperId = getGoalkeeperIdForTeam(teamId, metadata, workingFrames);
    const goalkeeper = metadata.players.find((p: any) => p.id === goalkeeperId);
    if (goalkeeper)
      goalkeeper.position = PlayPositions.GOALKEEPER;
  });
}

export const isHomeOnLeftSide = (fromTime: number, toTime: number, frames: StandardMatchFrame[], metadata: StandardMatchData) => {
  const workingFrames = frames.filter(f => f.timestamp >= fromTime && f.timestamp <= toTime);
  const homeGoalkeeperId = getGoalkeeperIdForTeam(metadata.home_team.id, metadata, workingFrames);
  const homeGoalkeeperAveragePosition = getGoalkeeperAveragePosition(workingFrames, homeGoalkeeperId);
  return homeGoalkeeperAveragePosition.y < 0;
}

export const parseTrackingData = (
  frames: any[],
  framerate: number,
  minTimeDifference: number,
  parseTime: (frame: any, framerate: number) => number,
  parseBall: (frame: any) => { ballX: number, ballY: number, ballZ: number, ballPossession: number, ballStatus: number },
  parsePlayers: (frame: any) => StandardPlayerFrame[],
  parsePeriod: (frame: any) => number
) => {

  const resultFrames: StandardMatchFrame[] = [];
  const startTime = frames.length > 0 ? parseTime(frames[0], framerate) : 0;
  let lastFrameTime = startTime;
  let processedFrames = 0;

  let reportedEmptyPlayers = false;

  for (let f = 0; f < frames.length; f++) {

    const frame = frames[f];
    const frameTime = parseTime(frame, framerate);

    // Sample frames at minTimeDifference second intervals
    if (frameTime > startTime && frameTime - lastFrameTime < minTimeDifference)
      continue;

    const players = parsePlayers(frame);

    // Sometimes frames have no positional data, skip these
    if (players.length === 0) {
      if (!reportedEmptyPlayers) {
        console.log('Empty players frame', frame, 'frame time', frameTime, 'startTime', startTime);
        reportedEmptyPlayers = true;
      }
      continue;
    }

    const ballData = parseBall(frame);

    resultFrames.push({
      id: f,
      period: parsePeriod(frame),
      timestamp: frameTime,
      ball: {
        x: ballData.ballX,
        y: ballData.ballY,
        z: ballData.ballZ,
        possessor_id: ballData.ballPossession
      },
      players: players
    });

    processedFrames++;
    lastFrameTime = frameTime;
  }

  return resultFrames;
};

export type DecimationFactors = {
  distance: number;
  angle: number;
  oscillationAngle: number;
  speed: number;
  acceleration: number;
}

const decimateFrames2 = (frames: ScenarioBallFrame[], threshold: DecimationFactors): ScenarioBallFrame[] => {  
  if (frames.length < 2) {
    console.warn('Not enough frames for decimation (need at least 2, got', frames.length, ')');
    return frames;
  }
  
  const resultFrames: ScenarioBallFrame[] = [];
  let fromFrame = frames[0];
  let fromAngle = Math.atan2(frames[1].Position.y - frames[0].Position.y, frames[1].Position.x - frames[0].Position.x) * 180 / Math.PI;
  let maxAngle = 0;
  resultFrames.push(fromFrame);
  for (let i = 2; i < frames.length; i++) {
    const frame = frames[i];
    const prevFrame = frames[i - 1];
    const angle = Math.atan2(frame.Position.y - prevFrame.Position.y, frame.Position.x - prevFrame.Position.x) * 180 / Math.PI;
    const distance = getDistance(frame.Position, fromFrame.Position);
    const angleDiff = angle - fromAngle;
    const absDiff = Math.abs(angleDiff);
    const prevMaxAngle = maxAngle;
    maxAngle = Math.max(maxAngle, absDiff);
    const oscillationAngle = maxAngle - absDiff;
    const height = frame.Position.z;

    if (
      height < 3 && ( // Prevent angle changes during high ball flights from being detected as a segment
        (
          absDiff > threshold.angle / Math.max(0.01, Math.min(1, frame.Speed / threshold.speed)) 
          || (prevMaxAngle !== 0 && oscillationAngle > threshold.oscillationAngle)
        ) 
        && distance > threshold.distance
      )
    ) {
      resultFrames.push(prevFrame);
      fromFrame = prevFrame;
      fromAngle = angle;
      maxAngle = 0;
      i++;
    }

  }
  if (resultFrames[resultFrames.length - 1] !== frames[frames.length - 1]) {
    resultFrames.push(frames[frames.length - 1]);
  }

  return resultFrames;
}

const getPossessionSegments = (ballFrames: ScenarioBallFrame[], players: ScenarioPlayer[], mergeSegments: boolean = true, thresholds: DecimationFactors = { distance: 1.5, angle: 20, oscillationAngle: 10, speed: 5, acceleration: 1 }, segmentMergeDistance: number = 3) => {
  const segments: ScenarioBallSegment[] = [];
  const crunchedFrames: ScenarioBallFrame[] = decimateFrames2(ballFrames, thresholds);
  let currentPlayer: ScenarioPlayer | undefined = crunchedFrames.length > 0 ? getNearestScenarioPlayer(players, crunchedFrames[0].Position, crunchedFrames[0].Time) : undefined;
  for (let i = 1; i < crunchedFrames.length - 1; i++) {

    // Some ball flights span multiple segments, so we should merge them until the ball is close to the end player

    let mergedFrames = 0;
    let startClosestPlayer;
    let endClosestPlayer;
    let segmentFrames: ScenarioBallFrame[] = [];
    do {
      startClosestPlayer = getNearestScenarioPlayer(players, crunchedFrames[i + mergedFrames].Position, crunchedFrames[i + mergedFrames].Time);
      endClosestPlayer = i + mergedFrames + 1 < crunchedFrames.length ? getNearestScenarioPlayer(players, crunchedFrames[i + mergedFrames + 1].Position, crunchedFrames[i + mergedFrames + 1].Time) : startClosestPlayer;
      if (startClosestPlayer !== currentPlayer || endClosestPlayer !== startClosestPlayer) {
        break;
      }
      mergedFrames++;
    } while (
      mergeSegments
      && !isUndefined(startClosestPlayer)
      && mergedFrames < (crunchedFrames.length - i - 1)
    );

    segmentFrames = ballFrames.filter(f => f.Time >= crunchedFrames[i - 1].Time && f.Time < crunchedFrames[i + mergedFrames].Time);

    if (segmentFrames.length > 0) {
      segments.push({
        frames: segmentFrames,
        fromPlayer: currentPlayer?.Hash,
        toPlayer: startClosestPlayer?.Hash,
        startPoint: segmentFrames[0].Position,
        endPoint: segmentFrames[segmentFrames.length - 1].Position,
        startTime: segmentFrames[0].Time,
        endTime: segmentFrames[segmentFrames.length - 1].Time
      });
    }
    currentPlayer = startClosestPlayer;
    i += mergedFrames;
  }

  const lastSegmentFrames = ballFrames.filter(f => f.Time >= crunchedFrames[crunchedFrames.length - 2].Time);

  if (lastSegmentFrames.length > 0) {
    segments.push({
      frames: lastSegmentFrames,
      fromPlayer: currentPlayer?.Hash,
      toPlayer: currentPlayer?.Hash,
      startPoint: lastSegmentFrames[0].Position,
      endPoint: lastSegmentFrames[lastSegmentFrames.length - 1].Position,
      startTime: lastSegmentFrames[0].Time,
      endTime: lastSegmentFrames[lastSegmentFrames.length - 1].Time
    });
  }

  return segments;
}

const adjustSegmentFrames = (
  segment: ScenarioBallSegment,
  targetPosition: { x: number; y: number; z: number },
  moveRatio: number,
  adjustmentMode: 'decreasing' | 'increasing',
  effectMultiplier: number = 1
) => {
  const segmentStartTime = segment.frames[0].Time;
  const segmentEndTime = segment.frames[segment.frames.length - 1].Time;
  const segmentDuration = segmentEndTime - segmentStartTime;

  for (let j = 0; j < segment.frames.length; j++) {
    const frame = segment.frames[j];
    const timeProgress = (frame.Time - segmentStartTime) / segmentDuration;

    let adjustmentStrength: number;
    if (adjustmentMode === 'decreasing') {
      // Stronger adjustment at start, weaker at end
      adjustmentStrength = Math.max(0, 1 - timeProgress);
    } else {
      // Weaker adjustment at start, stronger at end
      adjustmentStrength = timeProgress;
    }

    frame.Position.x = lerp(frame.Position.x, targetPosition.x, adjustmentStrength * moveRatio * effectMultiplier);
    frame.Position.y = lerp(frame.Position.y, targetPosition.y, adjustmentStrength * moveRatio * effectMultiplier);
  }
};

const ensureBallProximity = (ballSegments: ScenarioBallSegment[], players: ScenarioPlayer[], targetDistance: number) => {
  // For each segment, if the ball is not within target distance of the player, lerp the ball position to the player position
  for (let i = 0; i < ballSegments.length; i++) {
    // Start position of segment is where the player should be closest
    const segment = ballSegments[i];
    const fromPlayer = players.find(p => p.Hash === segment.fromPlayer);
    if (!fromPlayer) continue;
    const playerPosition = getScenarioPointAtTime(fromPlayer?.Route.Waypoints || [], segment.startTime);
    const ballPosition = segment.startPoint;
    const distance = getDistance(playerPosition.WorldPosition, ballPosition);

    if (distance > targetDistance) {
      // Calculate how much we need to move the ball towards the player
      const moveRatio = (distance - targetDistance) / distance;

      // Calculate the target position (closer to player)
      const targetPosition = {
        x: lerp(ballPosition.x, playerPosition.WorldPosition.x, moveRatio),
        y: lerp(ballPosition.y, playerPosition.WorldPosition.y, moveRatio),
        z: ballPosition.z // Keep z position unchanged
      };

      // Adjust frames in current segment with decreasing effect over time
      adjustSegmentFrames(segment, targetPosition, moveRatio, 'decreasing', 1);

      // If there's a previous segment, also adjust its frames to maintain continuity
      if (i > 0) {
        const prevSegment = ballSegments[i - 1];
        // Apply adjustment to previous segment frames with increasing effect towards the end
        adjustSegmentFrames(prevSegment, targetPosition, moveRatio, 'increasing', 1);
      }

      if (segment.fromPlayer === segment.toPlayer) {
        // Check if all frames have z = 0 (ball on ground, indicating dribbling)
        const allFramesOnGround = segment.frames.every(frame => frame.Position.z === 0);

        if (allFramesOnGround) {

          // For each frame, check if ball is behind the player and adjust if needed
          for (let j = 0; j < segment.frames.length; j++) {
            const frame = segment.frames[j];
            const playerPos = getScenarioPointAtTime(fromPlayer.Route.Waypoints || [], frame.Time);

            // Calculate player's direction (heading) based on route
            let playerDirection: Point;
            if (j === 0) {
              // For first frame, use direction to next waypoint or default direction
              const nextWaypoint = fromPlayer.Route.Waypoints.find(wp => wp.Time > frame.Time);
              if (nextWaypoint) {
                const dx = nextWaypoint.WorldPosition.x - playerPos.WorldPosition.x;
                const dy = nextWaypoint.WorldPosition.y - playerPos.WorldPosition.y;
                const length = Math.sqrt(dx * dx + dy * dy);
                playerDirection = { x: dx / length, y: dy / length };
              } else {
                // Default direction (forward)
                playerDirection = { x: 1, y: 0 };
              }
            } else {
              // Use direction from previous frame to current frame
              const prevFrame = segment.frames[j - 1];
              const prevPlayerPos = getScenarioPointAtTime(fromPlayer.Route.Waypoints || [], prevFrame.Time);
              const dx = playerPos.WorldPosition.x - prevPlayerPos.WorldPosition.x;
              const dy = playerPos.WorldPosition.y - prevPlayerPos.WorldPosition.y;
              const length = Math.sqrt(dx * dx + dy * dy);
              if (length > 0.01) { // Avoid division by zero
                playerDirection = { x: dx / length, y: dy / length };
              } else {
                // If player hasn't moved, use previous direction or default
                playerDirection = { x: 1, y: 0 };
              }
            }

            // Calculate vector from player to ball
            const ballToPlayer = {
              x: frame.Position.x - playerPos.WorldPosition.x,
              y: frame.Position.y - playerPos.WorldPosition.y
            };

            // Calculate dot product to determine if ball is behind player
            const dotProduct = ballToPlayer.x * playerDirection.x + ballToPlayer.y * playerDirection.y;

            const distance = getDistance(frame.Position, playerPos.WorldPosition);

            // If dot product is negative, ball is behind player
            if (dotProduct < 0) {
              // Move ball to be in front of player (at a small distance)
              const frontDistance = 0.25; // Distance in front of player
              frame.Position = {
                x: playerPos.WorldPosition.x + playerDirection.x * frontDistance,
                y: playerPos.WorldPosition.y + playerDirection.y * frontDistance,
                z: frame.Position.z
              }
            }
          }
        }
      }
    }
  }

  ballSegments.forEach(segment => {
    segment.startPoint = segment.frames[0].Position;
    segment.endPoint = segment.frames[segment.frames.length - 1].Position;
  });
};

const scoreLob = (segment: ScenarioBallSegment, players: ScenarioPlayer[]) => {
  let score = 0;
  const dist = getDistance(segment.frames[0].Position, segment.frames[segment.frames.length - 1].Position);
  if (dist > 30) {
    return 1;
  }
  if (dist > 5) {
    score += 0.25;
  }
  const passedThroughPlayers = players.filter(p => {
    // Ignore the from and to players
    if (p.Hash === segment.fromPlayer || p.Hash === segment.toPlayer) return false;

    return segment.frames.some(f => {
      const playerPos = getScenarioPointAtTime(p.Route.Waypoints || [], f.Time);
      const dist = getDistance(f.Position, playerPos.WorldPosition);
      return dist < 1;
    });
  });

  score += passedThroughPlayers.length * 0.25;

  return score;
}

const extrapolateBallData = (ballFrames: ScenarioBallFrame[], segments: ScenarioBallSegment[], players: ScenarioPlayer[]) => {

  // For each segment, calculate a ballistic trajectory and use that to calculate ball z position for each frame
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (!segment || !segment.frames || segment.frames.length === 0) continue;
    const fromPlayer = players.find(p => p.Hash === segment.fromPlayer);
    const g = 9.81; // gravity in m/s^2
    const t0 = segment.frames[0].Time;
    const t1 = segment.frames[segment.frames.length - 1].Time;
    const T = t1 - t0;

    // If the distance between the ball and player never exceeds 1m set the ball z to 0
    const maxDist = segment.frames.reduce((max, f) => {
      if (!fromPlayer) return max;
      const playerPos = getScenarioPointAtTime(fromPlayer.Route.Waypoints || [], f.Time);
      const dist = getDistance(f.Position, playerPos.WorldPosition);
      return Math.max(max, dist);
    }, 0);
    if (maxDist < 1) {
      segment.frames.forEach(f => { f.Position.z = 0; });
      continue;
    }

    const isLob = scoreLob(segment, players) > 0.75;
    if (!isLob) {
      segment.frames.forEach(f => { f.Position.z = 0; });
      continue;
    }

    const vz0 = 0.5 * g * T;
    for (let j = 0; j < segment.frames.length; j++) {
      const frame = segment.frames[j];
      const t = frame.Time - t0;
      let z = vz0 * t - 0.5 * g * t * t;
      if (z < 0) z = 0;
      frame.Position.z = z;
      frame.Possessor = segment.fromPlayer?.toString();
    }
  }

  return { ballFrames, segments };
}

/**
 * Adjusts ball height at specific frame indices and smooths transitions to neighboring frames across the entire scenario
 * @param allFrames - Array of all ball frames in the scenario
 * @param frameIndices - Array of frame indices to adjust to target height
 * @param targetHeight - The target Z position to set for the specified frames
 * @param priorTransitionFrames - Number of frames before the target frame to use for smoothing
 * @param postTransitionFrames - Number of frames after the target frame to use for smoothing
 * @param transitionStrength - Strength of the transition smoothing (default: 0.6)
 * @returns Number of frames that were adjusted
 */
const adjustBallHeight = (
  allFrames: ScenarioBallFrame[],
  frameIndices: number[],
  targetHeight: number,
  priorTransitionFrames: number,
  postTransitionFrames: number,
  transitionStrength: number = 0.6
): number => {
  let adjustedCount = 0;
  
  frameIndices.forEach(frameIndex => {
    const frame = allFrames[frameIndex];
    
    // Only adjust if the frame exists and needs adjustment
    if (frame && frame.Position.z !== targetHeight) {
      adjustedCount++;
      
      // Smooth transitions for neighboring frames across the entire scenario
      const transitionStart = Math.max(0, frameIndex - priorTransitionFrames);
      const transitionEnd = Math.min(allFrames.length - 1, frameIndex + postTransitionFrames);
      
      // Smooth frames around the target frame (excluding the target frame itself)
      for (let i = transitionStart; i <= transitionEnd; i++) {
        const currentFrame = allFrames[i];

        // Check for null/undefined frame or position before accessing properties
        if (!currentFrame || !currentFrame.Position) {
          continue;
        }

        if (i === frameIndex) {
          currentFrame.Position.z = targetHeight;
          continue;
        } 
        
        // Calculate transition strength based on distance from target frame
        let strength: number;
        if (i < frameIndex) {
          // Prior frames
          const distanceFromTarget = frameIndex - i;
          strength = 1 - (distanceFromTarget / (priorTransitionFrames + 1));
        } else {
          // Post frames
          const distanceFromTarget = i - frameIndex;
          strength = 1 - (distanceFromTarget / (postTransitionFrames + 1));
        }
        
        currentFrame.Position.z = lerp(currentFrame.Position.z, targetHeight, strength * transitionStrength);
      }
    }
  });
  
  return adjustedCount;
}

const ensureHeaderHeight = (ballSegments: ScenarioBallSegment[], allBallFrames: ScenarioBallFrame[]) => {
  const HEADER_HEIGHT = 1.65; // Header height for ball
  
  let headerCount = 0;
  const framesToAdjust: { frameIndex: number; priorFrames: number; postFrames: number }[] = [];
  
  ballSegments.forEach((segment, segmentIndex) => {
    // Check if this is a pass (fromPlayer !== toPlayer) and has frames
    if (segment.fromPlayer === segment.toPlayer || segment.frames.length === 0) {
      return;
    }
    
    // Check if the starting position is above header height
    const startFrame = segment.frames[0];
    if (startFrame.Position.z > HEADER_HEIGHT) {
      // Find the index of the start frame in the allBallFrames array using tolerance-based comparison
      const frameIndex = findFrameIndex(allBallFrames, startFrame);
      
      if (frameIndex !== -1) {
        // Calculate transition frame counts based on segment lengths
        const priorSegment = ballSegments[segmentIndex - 1];
        const priorFrames = priorSegment ? priorSegment.frames.length : 0;
        const postFrames = segment.frames.length;
        
        framesToAdjust.push({
          frameIndex,
          priorFrames,
          postFrames
        });
      }
    }
  });
  
  // Use the helper function to adjust all header frames with their specific transition counts
  framesToAdjust.forEach(({ frameIndex, priorFrames, postFrames }) => {
    headerCount += adjustBallHeight(
      allBallFrames,
      [frameIndex],
      HEADER_HEIGHT,
      priorFrames,
      postFrames,
      0.6 // Standard transition strength
    );
  });
  
}

const ensureBallBounces = (ballSegments: ScenarioBallSegment[], allBallFrames: ScenarioBallFrame[]) => {
  const GROUND_LEVEL = 0.11; // Ground level for ball
  
  let bounceCount = 0;
  const framesToAdjust: number[] = [];
  
  ballSegments.forEach(segment => {
    if (segment.frames.length < 3) return; // Need at least 3 frames to detect a local minimum
    
    // Find local minima (bottoms of bounces)
    const minima: number[] = [];
    
    for (let i = 1; i < segment.frames.length - 1; i++) {
      const prevFrame = segment.frames[i - 1];
      const currentFrame = segment.frames[i];
      const nextFrame = segment.frames[i + 1];
      
      // Check if current frame is a local minimum (lower than neighbors)
      const isLocalMinimum = currentFrame.Position.z < prevFrame.Position.z && 
                             currentFrame.Position.z < nextFrame.Position.z;
      
      if (isLocalMinimum) {
        minima.push(i);
      }
    }
    
    // Find frame indices in allBallFrames for minima that need adjustment
    minima.forEach(minIndex => {
      const frame = segment.frames[minIndex];
      
      // Only adjust if the minimum is above ground level
      if (frame.Position.z > GROUND_LEVEL) {
        // Find the index of this frame in the allBallFrames array using tolerance-based comparison
        const frameIndex = findFrameIndex(allBallFrames, frame);
        
        if (frameIndex !== -1) {
          framesToAdjust.push(frameIndex);
        }
      }
    });
  });
  
  // Use the helper function to adjust all bounce frames at once
  bounceCount = adjustBallHeight(
    allBallFrames,
    framesToAdjust,
    GROUND_LEVEL,
    12, // Prior transition frames
    12, // Post transition frames
    0.6
  );
  
}

const ensureBallGrounded = (ballSegments: ScenarioBallSegment[], allBallFrames: ScenarioBallFrame[]) => {
  const GROUND_LEVEL = 0.11;
  const MAX_AIR_THRESHOLD = 0.33; // Maximum z position to consider for grounding
  
  let count = 0;
  const framesToAdjust: number[] = [];
  
  ballSegments.forEach(segment => {
    if (segment.frames.length === 0) return;
    
    // Find continuous sections where ball z is above ground but below threshold
    const sections: { start: number; end: number }[] = [];
    let currentSection: { start: number; end: number } | null = null;
    
    for (let i = 0; i < segment.frames.length; i++) {
      const frame = segment.frames[i];
      const isInAir = frame.Position.z > GROUND_LEVEL && frame.Position.z < MAX_AIR_THRESHOLD;
      
      if (isInAir) {
        if (currentSection === null) {
          currentSection = { start: i, end: i };
        } else {
          currentSection.end = i;
        }
      } else {
        if (currentSection !== null) {
          sections.push(currentSection);
          currentSection = null;
        }
      }
    }
    
    // Add the last section if it exists
    if (currentSection !== null) {
      sections.push(currentSection);
    }

    // Find frame indices in allBallFrames for frames that need grounding
    sections.forEach(section => {
      for (let i = section.start; i <= section.end; i++) {
        const frame = segment.frames[i];
        
        // Find the index of this frame in the allBallFrames array using tolerance-based comparison
        const frameIndex = findFrameIndex(allBallFrames, frame);
        
        if (frameIndex !== -1) {
          framesToAdjust.push(frameIndex);
        }
      }
    });
  });
  
  // Use the helper function to adjust all grounding frames at once
  count = adjustBallHeight(
    allBallFrames,
    framesToAdjust,
    GROUND_LEVEL,
    12, // Prior transition frames
    12, // Post transition frames
    0.5 // Slightly lower transition strength for grounding
  );
  
}

const recalculateGroundPassFrames = (ballSegments: ScenarioBallSegment[], players: ScenarioPlayer[]) => {

  const BALL_RADIUS = 0.11;
  const ROLLING_FRICTION_COEFFICIENT = 0.07;
  const SLIDING_FRICTION_COEFFICIENT = 0.55;
  const GRAVITY = 9.81;
  let count = 0;

  ballSegments.forEach(segment => {
    const fromPlayer = players.find(p => p.Hash === segment.fromPlayer);
    const toPlayer = players.find(p => p.Hash === segment.toPlayer);
    if (!fromPlayer || !toPlayer || segment.fromPlayer === segment.toPlayer) return;

    const startPos = getScenarioPointAtTime(fromPlayer.Route.Waypoints || [], segment.startTime);
    const endPos = getScenarioPointAtTime(toPlayer.Route.Waypoints || [], segment.endTime);
    const isGroundPass = segment.frames.every(f => f.Position.z === 0);

    if (!isGroundPass) return;

    const dT = segment.endTime - segment.startTime;
    const dist = getDistance(startPos.WorldPosition, endPos.WorldPosition);

    // Calculate the transition speed from sliding to rolling
    const transitionSpeed = Math.sqrt(2 * GRAVITY * BALL_RADIUS * (SLIDING_FRICTION_COEFFICIENT - ROLLING_FRICTION_COEFFICIENT));

    // Calculate required initial speed to reach target with physics constraints
    // We need to solve: distance = initialSpeed * time - 0.5 * deceleration * time^2
    // But we need to account for the transition from sliding to rolling

    const rollingDeceleration = ROLLING_FRICTION_COEFFICIENT * GRAVITY;
    const requiredSpeedForRolling = (dist + 0.5 * rollingDeceleration * dT * dT) / dT;
    let initialSpeed: number;
    let frictionProfile: { time: number; coeff: number }[] = [];
    if (requiredSpeedForRolling <= transitionSpeed) {
      initialSpeed = requiredSpeedForRolling;
      frictionProfile = [{ time: 0, coeff: ROLLING_FRICTION_COEFFICIENT }];
    } else {
      const slidingDeceleration = SLIDING_FRICTION_COEFFICIENT * GRAVITY;
      const requiredSpeedForSliding = (dist + 0.5 * slidingDeceleration * dT * dT) / dT;
      initialSpeed = requiredSpeedForSliding;
      frictionProfile = [{ time: 0, coeff: SLIDING_FRICTION_COEFFICIENT }];
    }

    // Recalculate positions for each frame using physics with target constraint
    let currentSpeed = initialSpeed;
    let totalDistanceTraveled = 0;

    segment.frames.forEach((frame, index) => {
      const frameTime = frame.Time - segment.startTime;

      if (frameTime <= 0) {
        frame.Position.x = startPos.WorldPosition.x;
        frame.Position.y = startPos.WorldPosition.y;
        frame.Position.z = 0;
        return;
      }

      const prevFrameTime = index > 0 ? segment.frames[index - 1].Time - segment.startTime : 0;
      const deltaTime = frameTime - prevFrameTime;

      let currentFrictionCoeff = ROLLING_FRICTION_COEFFICIENT;
      for (let i = 0; i < frictionProfile.length - 1; i++) {
        if (frameTime >= frictionProfile[i].time && frameTime < frictionProfile[i + 1].time) {
          currentFrictionCoeff = frictionProfile[i].coeff;
          break;
        }
      }
      if (frameTime >= frictionProfile[frictionProfile.length - 1].time) {
        currentFrictionCoeff = frictionProfile[frictionProfile.length - 1].coeff;
      }

      const deceleration = currentFrictionCoeff * GRAVITY;
      currentSpeed = Math.max(0, currentSpeed - deceleration * deltaTime);
      const averageSpeed = currentSpeed + (deceleration * deltaTime / 2);
      const stepDistance = averageSpeed * deltaTime;
      totalDistanceTraveled += stepDistance;

      if (index === segment.frames.length - 1) {
        totalDistanceTraveled = dist;
      }

      const progress = Math.min(1, totalDistanceTraveled / dist);

      // Interpolate position along the straight line path
      frame.Position.x = startPos.WorldPosition.x + progress * (endPos.WorldPosition.x - startPos.WorldPosition.x);
      frame.Position.y = startPos.WorldPosition.y + progress * (endPos.WorldPosition.y - startPos.WorldPosition.y);
      frame.Position.z = 0; // Ground pass - always on ground
    });
    count++;
  });

  return ballSegments;
};

export const generateScenario = (
  frames: StandardMatchFrame[],
  metadata: StandardMatchData,
  events: StandardEvent[],
  startTime: number,
  fromTime: number,
  toTime: number,
  mirrorX: boolean,
  mirrorY: boolean,
  startFrame: number,
  generatorSettings: GeneratorSettings = DefaultGeneratorSettings,
) => {

  const pitchLength = metadata?.pitch_length || 104;
  const pitchWidth = metadata?.pitch_width || 70;
  let ballFrames: ScenarioBallFrame[] = [];
  let ballSegments: ScenarioBallSegment[] = [];
  let endTime = 0;
  let lastFrameTime = 0;
  let processedFrames = 0;
  let debugOutput = false;
  let lastFrame = 0;
  const playerIds: string[] = [];
  
  guessGoalkeepers(metadata, frames);

  const isHomeLeft = isHomeOnLeftSide(fromTime, toTime, frames, metadata);

  const scenarioData: ScenarioData = {
    EditorVersion: '3.0.0',
    Name: 'Imported Scenario',
    EndTime: 0,
    Players: [],
    InitialBallPos: {
      x: 0,
      y: 0
    },
    BallFrames: [],
    DebugBallFrames: [],
    Pitch: {
      Length: pitchLength,
      Width: pitchWidth,
      Goals: [
        {
          Team: 1,
          Position: {
            x: 0,
            y: isHomeLeft ? -pitchLength / 2 : pitchLength / 2
          }
        },
        {
          Team: 2,
          Position: {
            x: 0,
            y: isHomeLeft ? pitchLength / 2 : -pitchLength / 2
          }
        }
      ]
    }
  };

  const playerMap = new Map<string, StandardPlayerData>();
  metadata.players.forEach((player) => {
    playerMap.set(player.id, player);
  });

  // Binary search for the start frame
  let startFrameIndex = 0;
  let endFrameIndex = frames.length - 1;
  while (startFrameIndex <= endFrameIndex) {
    const midFrameIndex = Math.floor((startFrameIndex + endFrameIndex) / 2);
    const midFrameTime = frames[midFrameIndex].timestamp;
    if (midFrameTime < fromTime) {
      startFrameIndex = midFrameIndex + 1;
    } else {
      endFrameIndex = midFrameIndex - 1;
    }
  }
  startFrame = startFrameIndex;

  for (let f = startFrame; f < frames.length; f++) {

    // rawTime:      the time read from the frame
    // frameTime:    the time relative to the start of the data
    // scenarioTime: the time relative to the start of selected range (fromTime, toTime)

    const frame = frames[f];
    const frameTime = (frame.timestamp);
    const scenarioTime = frameTime - fromTime;
    lastFrame = f;

    if (frameTime < fromTime)
      continue;

    if (frameTime > toTime) {
      break;
    }

    // Store ball position and time for later processing
    const ballZ = frame.ball.z || 0;
    const { newX: ballX, newY: ballY } = transformPosition(frame.ball.x, frame.ball.y, mirrorX, mirrorY);

    const ballFrame: ScenarioBallFrame = {
      Time: scenarioTime,
      Position: {
        x: ballX,
        y: ballY,
        z: ballZ
      },
      Possessor: frame.ball.possessor_id?.toString(),
      Speed: 0,
      Acceleration: 0,
      Direction: 0
    };

    const lastBallFrame = ballFrames[ballFrames.length - 1];
    if (lastBallFrame) {
      ballFrame.Speed = calculateVelocity(lastBallFrame, ballFrame, scenarioTime - lastBallFrame.Time);
      ballFrame.Acceleration = ballFrame.Speed - lastBallFrame.Speed;
      ballFrame.Direction = Math.atan2(ballFrame.Position.y - lastBallFrame.Position.y, ballFrame.Position.x - lastBallFrame.Position.x) * 180 / Math.PI;
    }
    ballFrames.push(ballFrame);

    // Sample frames at 1 second intervals
    if (frameTime > 0 && frameTime - lastFrameTime < 1)
      continue;

    processedFrames++;
    endTime = Math.max(endTime, scenarioTime);

    // Process player data into player objects and positions
    const players = frame.players;

    // Sometimes frames have no positional data, skip these
    if (players.length === 0)
      continue;

    for (let p = 0; p < players.length; p++) {
      const player = players[p];
      const playerInfo = playerMap.get(player.id);
      if (!playerInfo) {
        continue;
      }
      const { newX: playerX, newY: playerY } = transformPosition(player.x, player.y, mirrorX, mirrorY);

      if (!playerIds.includes(player.id)) {
        playerIds.push(player.id);
        scenarioData.Players.push({
          id: player.id,
          Hash: playerIds.length - 1,
          PlayerName: playerInfo.short_name,
          JerseyNumber: playerInfo.jersey_number,
          IsPlayer: false,
          Team: playerInfo.team_id === metadata.home_team.id ? 1 : 2,
          Position: playerInfo.position as PlayPositions,
          Route: {
            Actions: [],
            Waypoints: []
          }
        });
      }

      // If we have skipped frames at the start of the data, add a waypoint at time 0
      const scenarioPlayer = scenarioData.Players.find(p => p.id === player.id);
      if (isEmpty(scenarioPlayer?.Route.Waypoints) && scenarioTime > 0) {
        addWaypoint(scenarioPlayer?.Route.Waypoints || [], 0, { x: playerX, y: playerY });
      }

      // Add waypoint for current position at current time
      addWaypoint(scenarioPlayer?.Route.Waypoints || [], scenarioTime, { x: playerX, y: playerY });
    }

    lastFrameTime = frameTime;
  }

  scenarioData.DebugBallFrames = JSON.parse(JSON.stringify(ballFrames));

  // Place the initial ball position at the closest player to the first ball frame
  if (!isEmpty(ballFrames)) {
    const closestPlayer = getNearestScenarioPlayer(scenarioData.Players, ballFrames[0].Position, 0);
    scenarioData.InitialBallPos = closestPlayer ? closestPlayer.Route.Waypoints[0].WorldPosition : { x: 0, y: 0 };
  }
  else {
    scenarioData.InitialBallPos = { x: 0, y: 0 };
  }

  scenarioData.EndTime = endTime;

  // Add player actions from events
  // TODO: Add tackle events
  // const timeframeEvents = events.filter((event: StandardEvent) => event.start_time >= fromTime && event.start_time <= toTime);
  // const tackleEvents = timeframeEvents.filter((event: any) => event.type === 'tackle');
  // tackleEvents.forEach((event: any) => {
  //   const eventTime = getDFLEventTime(event, startTime) + fromTime;
  //   const winner = scenarioData.Players.find((p) => p.id === event.tacklingGame.winner);
  //   const loserIndex = scenarioData.Players.findIndex((p) => p.id === event.tacklingGame.loser);
  //   const loser = scenarioData.Players[loserIndex];
  //   if (winner && loser) {
  //     const tackleAction = {
  //       enabled: true,
  //       type: ScenarioActionType.GetBall,
  //       startTime: eventTime,
  //       endTime: eventTime + 0.5,
  //       target: loserIndex,
  //       location: { WorldPosition: { x: 0, y: 0 } },
  //       data: null,
  //       index: 0,
  //       height: 0,
  //       direction: 0,
  //       time: 0
  //     };

  //     winner.Route.Actions.push(tackleAction);
  //   }
  // });

  ballSegments = getPossessionSegments(ballFrames, scenarioData.Players, generatorSettings.mergeSegments, generatorSettings.decimationThresholds, generatorSettings.segmentMergeDistance);

  if (generatorSettings.doEnsureBallProximity)
    ensureBallProximity(ballSegments, scenarioData.Players, generatorSettings.minProximity || DefaultGeneratorSettings.minProximity || 0.25);

  if (ballFrames.every(f => !f.Position.z)) {
    const { ballFrames: extrapolatedBallFrames, segments: extrapolatedBallSegments } = extrapolateBallData(ballFrames, ballSegments, scenarioData.Players);
    ballFrames = extrapolatedBallFrames;
    ballSegments = extrapolatedBallSegments;
  }

  ensureBallGrounded(ballSegments, ballFrames);
  ensureBallBounces(ballSegments, ballFrames);
  ensureHeaderHeight(ballSegments, ballFrames);

  if (generatorSettings.recalculateGroundPasses)
    ballSegments = recalculateGroundPassFrames(ballSegments, scenarioData.Players);

  addPassesFromBallFrames(ballSegments, scenarioData.Players);
  
  scenarioData.BallFrames = ballSegments.flatMap(segment => segment.frames.map(f => {
    return {
      Time: f.Time,
      Position: f.Position,
      Possessor: segment.fromPlayer?.toString(),
      Speed: f.Speed,
      Acceleration: f.Acceleration,
      Direction: f.Direction
    }
  }));

  return { scenarioData, ballFrames, ballSegments, startTime, lastFrame };
};

export const prepareFrameData = (rawFrameData: string, prepareFrame: (frame: string) => any, filterFrame: (frame: any) => boolean) => {
  return rawFrameData
    .split('\n')
    .map((frame: string) => prepareFrame(frame))
    .filter((frame: any) => filterFrame(frame));
}